import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { 
  CreditCard, 
  Plus, 
  History, 
  PlusCircle, 
  Search, 
  Filter, 
  ChevronRight, 
  Download,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  X,
  QrCode,
  Trash2,
  Printer
} from 'lucide-react';
import OfficialBillPrint from '../components/OfficialBillPrint';
import StudentAvatar from '../components/StudentAvatar';

const Payments = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ student_id: '', total_amount: '', paid_amount: '', payment_date: '', paid_date: '' });
  const [filterType, setFilterType] = useState('All'); 
  const [selectedCountry, setSelectedCountry] = useState('All');
  
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [printHistory, setPrintHistory] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState('transactions'); // 'transactions' or 'prints'
  const [showInstallmentForm, setShowInstallmentForm] = useState(false);
  const [instForm, setInstForm] = useState({ amount: '', installment_date: '' });
  const [showQR, setShowQR] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('Cash');

  const handlePrintBill = (payment) => {
    const student = students.find(s => s.id === payment.student_id);
    if (student) {
      setPrintData({ student, payment });
      setBillItems([{
        description: `Consultancy & Processing Fee (${student.visa_type} Visa - ${student.abroad_study_country || 'Various'})`,
        amount: payment.paid_amount
      }]);
    } else {
      alert('Could not find student details for this bill.');
    }
  };

  const executePrint = async () => {
    window.print();
    
    // We use a small timeout to allow the print dialog to close before showing the confirmation
    setTimeout(async () => {
      const isSuccess = window.confirm("Did the bill print successfully? Click 'OK' to record this in the print logs.");
      
      if (isSuccess && printData?.payment?.id) {
        try {
          const totalAmount = billItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
          await api.post(`/payments/${printData.payment.id}/print-log`, {
            amount: totalAmount,
            payment_method: paymentMethod
          });
          alert('Print action has been recorded in the history.');
        } catch (err) {
          console.error("Failed to log print action:", err);
        }
      }
    }, 500);
  };

  const fetchData = async () => {
    try {
      const [payRes, stuRes, counRes] = await Promise.all([
        api.get('/payments'),
        api.get('/students'),
        api.get('/countries')
      ]);
      setPayments(payRes.data);
      setStudents(stuRes.data);
      setCountries(counRes.data.filter(c => c.status === 'active'));
      return payRes.data;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments', formData);
      fetchData();
      setShowForm(false);
      setFormData({ student_id: '', total_amount: '', paid_amount: '', payment_date: '', paid_date: '' });
    } catch (err) {
      alert('Error recording payment');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setFormData({ student_id: '', total_amount: '', paid_amount: '', payment_date: '', paid_date: '' });
  };

  const handleViewHistory = async (bill) => {
    setSelectedBill(bill);
    try {
      const [instRes, printRes] = await Promise.all([
        api.get(`/payments/${bill.id}/installments`),
        api.get(`/payments/${bill.id}/print-history`)
      ]);
      setHistoryData(instRes.data);
      setPrintHistory(printRes.data);
      setActiveHistoryTab('transactions');
      setShowHistory(true);
    } catch (err) {
      alert('Error fetching history data');
    }
  };

  const submitInstallment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments/installments', {
        payment_id: selectedBill.id,
        ...instForm
      });
      setShowInstallmentForm(false);
      setInstForm({ amount: '', installment_date: '' });
      const updatedPayments = await fetchData();
      if (updatedPayments) {
        const freshBill = updatedPayments.find(p => p.id === selectedBill.id);
        if (freshBill) {
          setSelectedBill(freshBill);
          handleViewHistory(freshBill);
        }
      }
    } catch (err) {
      alert('Error adding installment');
    }
  };
 
  const handleDelete = async (p) => {
      if (!window.confirm(`Permanently delete billing record for ${p.student_name}? This will remove all payment history for this bill.`)) return;
      try {
          await api.delete(`/payments/${p.id}`);
          fetchData();
      } catch (err) {
          alert('Error deleting payment record');
      }
  };

  const filteredPayments = payments.filter(p => {
    const matchesVisa = filterType === 'All' || p.visa_type === filterType;
    const matchesCountry = (filterType !== 'Student') || (selectedCountry === 'All' || p.abroad_study_country === selectedCountry);
    return matchesVisa && matchesCountry;
  });

  const availableCountries = countries.map(c => c.name).sort();
  
  const summaryStats = filteredPayments.reduce((acc, curr) => {
    const total = parseFloat(curr.total_amount) || 0;
    const paid = parseFloat(curr.paid_amount) || 0;
    const due = total - paid;
    acc.total += total;
    acc.paid += paid;
    acc.due += Math.max(0, due);
    return acc;
  }, { total: 0, paid: 0, due: 0 });

  const currentDue = (parseFloat(formData.total_amount || 0) - parseFloat(formData.paid_amount || 0));

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-bold">Loading Payments...</p>
      </div>
    </div>
  );

  return (
    <div className="animate-in fade-in duration-500">
      <div className="space-y-8 no-print">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Payments & Billing</h1>
          <p className="text-sm text-gray-500 font-semibold">Monitor invoices, payments, and outstanding balances.</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary" onClick={() => setShowQR(true)}>
             <QrCode size={18} />
             Payment QR
          </button>
          <button className="btn-secondary">
             <Download size={18} />
             Export PDF
          </button>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? <X size={18} /> : <Plus size={18} />}
            {showForm ? 'Cancel' : 'Add Payment'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Total Invoiced */}
        <div className="bg-blue-600 p-5 rounded-[1.5rem] shadow-lg shadow-blue-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:scale-[1.02] transition-all text-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <CreditCard size={20} />
            </div>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Total Amount</span>
          </div>
          <h4 className="text-3xl font-black">NRs {summaryStats.total.toLocaleString()}</h4>
          <div className="flex items-center gap-2 opacity-80 font-bold text-[10px] uppercase tracking-tighter">
            <Clock size={12} />
            <span>Updated just now</span>
          </div>
        </div>
        
        {/* Amount Received */}
        <div className="bg-emerald-500 p-5 rounded-[1.5rem] shadow-lg shadow-emerald-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:scale-[1.02] transition-all text-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white text-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
              <CheckCircle2 size={20} />
            </div>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Total Paid</span>
          </div>
          <h4 className="text-3xl font-black">NRs {summaryStats.paid.toLocaleString()}</h4>
          <div className="flex items-center gap-2 opacity-80 font-bold text-[10px] uppercase tracking-tighter">
            <ArrowUpRight size={12} />
            <span>+15.4% growth</span>
          </div>
        </div>

        {/* Outstanding Due */}
        <div className="bg-rose-500 p-5 rounded-[1.5rem] shadow-lg shadow-rose-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:scale-[1.02] transition-all text-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white text-rose-600 rounded-xl flex items-center justify-center shadow-sm">
              <AlertCircle size={20} />
            </div>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Remaining Due</span>
          </div>
          <h4 className="text-3xl font-black">NRs {summaryStats.due.toLocaleString()}</h4>
          <div className="flex items-center gap-2 opacity-80 font-bold text-[10px] uppercase tracking-tighter">
            <AlertCircle size={12} />
            <span>Across {filteredPayments.filter(p => p.status !== 'paid').length} accounts</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between no-print">
        <div className="flex bg-white p-1 rounded-2xl border border-gray-100 shadow-sm w-fit overflow-x-auto scrollbar-hide">
          {['All', 'Student', 'Working'].map(type => (
            <button
              key={type}
              onClick={() => { setFilterType(type); setSelectedCountry('All'); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 whitespace-nowrap ${
                filterType === type 
                ? 'bg-indigo-600 text-white shadow-md' 
                : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {type === 'All' ? 'All Records' : `${type} Visa`}
            </button>
          ))}
        </div>
        
        <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm flex-1 max-w-md">
           <Search size={18} className="text-gray-400" />
           <input type="text" placeholder="Search students or invoices..." className="bg-transparent border-none outline-none text-sm w-full font-medium" />
        </div>
      </div>

      {filterType === 'Student' && availableCountries.length > 0 && (
        <div className="p-4 bg-white rounded-3xl border border-gray-100 shadow-sm no-print space-y-3">
          <div className="flex items-center gap-2 px-2">
            <Filter size={14} className="text-indigo-600" />
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Filter by Country</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCountry('All')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                selectedCountry === 'All' 
                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' 
                : 'bg-gray-50 text-gray-500 border border-transparent hover:bg-gray-100'
              }`}
            >
              All Countries
            </button>
            {availableCountries.map(country => (
              <button
                key={country}
                onClick={() => setSelectedCountry(country)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  selectedCountry === country 
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' 
                  : 'bg-gray-50 text-gray-500 border border-transparent hover:bg-gray-100'
                }`}
              >
                {country}
              </button>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="card bg-indigo-50/50 border-indigo-200 border-2 border-dashed animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-indigo-600 rounded-xl text-white">
              <PlusCircle size={20} />
            </div>
            <h2 className="text-lg font-black text-indigo-900 tracking-tight">Add Payment</h2>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-1.5">
              <label className="block text-xs font-black text-indigo-900 uppercase tracking-widest ml-1">Student Name</label>
              <select className="input-field !bg-white" value={formData.student_id} onChange={e => setFormData({...formData, student_id: e.target.value})} required>
                <option value="">Select Student</option>
                {students
                  .filter(s => filterType === 'All' || s.visa_type === filterType)
                  .map(s => <option key={s.id} value={s.id}>{s.full_name} ({s.visa_type})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-black text-indigo-900 uppercase tracking-widest ml-1">Total Bill Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-2.5 text-indigo-400 font-bold text-sm">NRs</span>
                <input type="number" step="0.01" className="input-field pl-16" placeholder="0.00" value={formData.total_amount} onChange={e => setFormData({...formData, total_amount: e.target.value})} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-black text-indigo-900 uppercase tracking-widest ml-1">Initial Paid Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-2.5 text-indigo-400 font-bold text-sm">NRs</span>
                <input type="number" step="0.01" className="input-field pl-16" placeholder="0.00" value={formData.paid_amount} onChange={e => setFormData({...formData, paid_amount: e.target.value})} required />
              </div>
            </div>
            <div className="p-4 bg-white/80 rounded-2xl border border-indigo-100 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Remaining Balance</span>
                <p className={`text-xl font-black mt-0.5 ${currentDue > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  NRs {currentDue.toLocaleString()}
                </p>
              </div>
              <div className={`p-2 rounded-full ${currentDue > 0 ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-500'}`}>
                {currentDue > 0 ? <AlertCircle size={20} /> : <CheckCircle2 size={20} />}
              </div>
            </div>
            <div className="lg:col-span-2 flex justify-end items-end gap-3">
              <button type="button" onClick={handleCancelForm} className="px-6 py-2.5 text-gray-500 font-bold text-sm hover:bg-white rounded-xl transition-all">Cancel</button>
              <button type="submit" className="btn-primary !px-12">
                Save Payment
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest">Student Name</th>
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest">Visa & Status</th>
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-right">Total</th>
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-right">Paid</th>
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-right text-rose-500">Balance</th>
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest">Added By</th>
                <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredPayments.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-2">
                       <CreditCard size={48} className="text-gray-100" />
                       <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No records found</p>
                    </div>
                  </td>
                </tr>
              )}
              {filteredPayments.map(p => {
                const due = parseFloat(p.total_amount) - parseFloat(p.paid_amount);
                const isPaid = p.status === 'paid';
                return (
                  <tr key={p.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <StudentAvatar
                          photoUrl={students.find(s => s.id === p.student_id)?.photo_url}
                          fullName={p.student_name}
                          className="w-10 h-10 rounded-xl"
                        />
                        <div>
                          <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{p.student_name}</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : 'No Date'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded uppercase">{p.visa_type}</span>
                        <span className={`status-badge ${isPaid ? 'status-active' : 'status-danger'}`}>
                          {isPaid ? 'Settled' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-bold text-gray-900">NRs {parseFloat(p.total_amount).toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-emerald-600">
                      NRs {parseFloat(p.paid_amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-1 rounded-lg font-black ${due > 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                        NRs {Math.max(0, due).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs font-bold text-gray-700">{p.creator_name || 'System'}</p>
                      {p.modifier_name && p.modifier_name !== p.creator_name && (
                        <p className="text-[9px] text-indigo-500 font-bold uppercase tracking-tight">Modified by {p.modifier_name}</p>
                      )}
                      <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
                        {p.updated_at && p.updated_at !== p.created_at ? 'Modified' : 'Created'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-2">
                        <button onClick={() => handleViewHistory(p)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="View History">
                          <History size={18} />
                        </button>
                        {p.visa_type !== 'Student' && (
                          <button onClick={() => handlePrintBill(p)} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all" title="Print Bill">
                            <Printer size={18} />
                          </button>
                        )}
                        {user?.role === 'Admin' && (
                          <button 
                            onClick={() => handleDelete(p)}
                            className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                            title="Delete Bill"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                        <ChevronRight className="text-gray-200 group-hover:text-gray-400 transition-colors" size={16} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attractive Payment History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header with Stats */}
            <div className="p-8 bg-blue-600 text-white relative">
              <button 
                onClick={() => setShowHistory(false)} 
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all border border-white/10"
              >
                <X size={20} />
              </button>
              
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-white text-blue-600 flex items-center justify-center shadow-lg shadow-blue-900/20">
                  <History size={28} />
                </div>
                <div>
                   <h2 className="text-2xl font-black tracking-tight leading-none mb-1">Payment History</h2>
                   <p className="text-blue-100 text-xs font-bold uppercase tracking-[0.2em]">{selectedBill?.student_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                  <p className="text-[9px] text-blue-100 font-black uppercase tracking-widest mb-1 opacity-70">Total Invoice</p>
                  <p className="text-lg font-black truncate">NRs {parseFloat(selectedBill?.total_amount).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                  <p className="text-[9px] text-blue-100 font-black uppercase tracking-widest mb-1 opacity-70">Paid Amount</p>
                  <p className="text-lg font-black text-emerald-300 truncate">NRs {parseFloat(selectedBill?.paid_amount || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-white rounded-2xl border border-white hover:bg-gray-50 transition-all shadow-lg shadow-blue-900/10">
                  <p className="text-[9px] text-blue-600 font-black uppercase tracking-widest mb-1">Remaining</p>
                  <p className="text-lg font-black text-rose-600 truncate">NRs {(parseFloat(selectedBill?.total_amount || 0) - parseFloat(selectedBill?.paid_amount || 0)).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            {/* Tabbed History Body */}
            <div className="flex-1 overflow-y-auto p-8 bg-gray-50/50">
               {/* Tab Switcher */}
               <div className="flex bg-white p-1.5 rounded-2xl border border-gray-100 shadow-sm mb-8">
                  <button 
                    onClick={() => setActiveHistoryTab('transactions')}
                    className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeHistoryTab === 'transactions' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}
                  >
                    Payment History
                  </button>
                  <button 
                    onClick={() => setActiveHistoryTab('prints')}
                    className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeHistoryTab === 'prints' ? 'bg-rose-500 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}
                  >
                    Print Logs
                  </button>
               </div>

               {activeHistoryTab === 'transactions' ? (
                 <div className="space-y-4 animate-in slide-in-from-left-4 duration-300">
                    <div className="flex items-center justify-between px-2">
                      <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Partial Payments</h3>
                      <span className="text-[10px] font-bold text-gray-400">{historyData.length} Records</span>
                    </div>

                    {historyData.length === 0 ? (
                      <div className="py-24 text-center flex flex-col items-center gap-4 opacity-30 grayscale bg-white rounded-[2.5rem] border border-dashed border-gray-200">
                        <CreditCard size={64} />
                        <p className="font-black uppercase tracking-widest text-xs">No payments found</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {historyData.map((h, i) => (
                          <div key={h.id} className="flex items-center gap-4 p-6 bg-white border border-gray-100 rounded-3xl hover:border-emerald-200 transition-all shadow-sm hover:shadow-md">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                              <CheckCircle2 size={24} />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <p className="text-xl font-black text-gray-900 leading-none">NRs {parseFloat(h.amount).toLocaleString()}</p>
                                <span className="text-[10px] font-bold text-gray-300">#{h.id}</span>
                              </div>
                              <p className="text-[10px] text-gray-500 font-black uppercase tracking-wider mt-2.5 flex items-center gap-1.5">
                                <Clock size={12} className="text-blue-500" />
                                {new Date(h.installment_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                 </div>
               ) : (
                 <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                    <div className="flex items-center justify-between px-2">
                      <h3 className="text-[10px] font-black text-rose-400 uppercase tracking-[0.2em]">Bill Print History</h3>
                      <span className="text-[10px] font-bold text-rose-300">{printHistory.length} Prints</span>
                    </div>

                    {printHistory.length === 0 ? (
                      <div className="py-24 text-center flex flex-col items-center gap-4 opacity-30 grayscale bg-white rounded-[2.5rem] border border-dashed border-gray-200">
                        <Printer size={64} />
                        <p className="font-black uppercase tracking-widest text-xs">Never printed yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {printHistory.map((log, i) => (
                          <div key={log.id} className="flex items-center justify-between p-5 bg-white border border-gray-100 rounded-3xl hover:border-rose-100 transition-all shadow-sm hover:shadow-md">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center">
                                <Printer size={20} />
                              </div>
                              <div>
                                <p className="text-xs font-black text-gray-900 uppercase">{log.username || 'Staff'}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Amount: NRs {parseFloat(log.amount || 0).toLocaleString()}</span>
                                  <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                  <span className="text-[10px] text-indigo-500 font-black uppercase tracking-tighter">{log.payment_method || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-[11px] font-black text-gray-700">{new Date(log.printed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase">{new Date(log.printed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                 </div>
               )}
            </div>

            {/* Modal Footer Actions */}
            <div className="p-8 bg-white border-t border-gray-100 flex items-center gap-4">
               <button 
                 onClick={() => setShowHistory(false)} 
                 className="flex-1 py-4 px-6 bg-gray-50 border border-gray-100 rounded-2xl font-black text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all text-[11px] uppercase tracking-[0.2em]"
               >
                 Close History
               </button>
               {selectedBill?.status !== 'paid' && (
                  <button 
                    onClick={() => setShowInstallmentForm(true)}
                    className="flex-[2] py-4 px-6 bg-blue-600 text-white rounded-2xl font-black shadow-xl shadow-blue-500/20 hover:bg-blue-700 active:scale-95 transition-all text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 group"
                  >
                    <PlusCircle size={20} className="group-hover:rotate-90 transition-transform" />
                    Add Payment
                  </button>
               )}
            </div>
          </div>
        </div>
      )}

      {/* Add Installment Modal */}
      {showInstallmentForm && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md z-[110] flex items-center justify-center p-4 animate-in zoom-in-95 duration-200">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-sm w-full p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-emerald-500"></div>
            <div className="mb-8 flex flex-col items-center text-center">
               <div className="w-20 h-20 rounded-[2rem] bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6 shadow-sm shadow-emerald-100">
                  <CreditCard size={32} />
               </div>
               <h2 className="text-2xl font-black text-gray-900 tracking-tight">Add Payment</h2>
               <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-2">Bill Ref: #{selectedBill?.id}</p>
            </div>
            <form onSubmit={submitInstallment} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Payment Amount</label>
                <div className="relative">
                   <span className="absolute left-5 top-3.5 text-emerald-500 font-black text-sm">NRs</span>
                   <input 
                    type="number" 
                    className="input-field pl-16 !bg-gray-50 focus:!bg-white !py-4 !text-lg font-black" 
                    placeholder="0.00"
                    value={instForm.amount} 
                    onChange={e => setInstForm({...instForm, amount: e.target.value})} 
                    max={parseFloat(selectedBill?.total_amount) - parseFloat(selectedBill?.paid_amount)}
                    required 
                   />
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button type="submit" className="w-full bg-emerald-600 text-white py-4 rounded-[1.5rem] font-black shadow-xl shadow-emerald-100 hover:bg-emerald-700 active:scale-95 transition-all text-sm uppercase tracking-widest">Save Payment</button>
                <button type="button" onClick={() => setShowInstallmentForm(false)} className="w-full py-4 text-gray-400 font-black text-xs uppercase tracking-widest hover:text-gray-600 transition-colors">Discard Change</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[120] flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-sm w-full p-2 relative overflow-hidden">
            <div className="absolute top-4 right-4 z-10">
               <button onClick={() => setShowQR(false)} className="p-2 bg-black/5 hover:bg-black/10 rounded-full transition-all">
                 <X size={20} className="text-gray-600" />
               </button>
            </div>
            <div className="rounded-[2rem] overflow-hidden">
              <img src="/qr_code.jpg" alt="Payment QR" className="w-full h-auto" />
            </div>
            <div className="p-6 text-center">
              <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Scan to Pay via Fonepay</p>
              <p className="text-sm font-bold text-gray-900">Global IME Bank</p>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Bill Print Modal */}
      {printData && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[150] overflow-y-auto no-print">
          <div className="min-h-screen px-4 text-center">
            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
            <div className="inline-block w-full max-w-4xl p-6 my-8 text-left align-middle transition-all transform bg-gray-100 shadow-2xl rounded-2xl">
              <div className="flex justify-between items-center mb-6">
                <div className="flex flex-col gap-2">
                   <h3 className="text-xl font-black text-gray-900">Official Bill Preview</h3>
                   <p className="text-xs font-semibold text-gray-500">Edit the descriptions directly inside the bill below.</p>
                </div>
                <div className="flex gap-3 items-start">
                  <button 
                    onClick={executePrint}
                    className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all active:scale-95"
                  >
                    <Printer size={18} />
                    Print Official Bill
                  </button>
                  <button 
                    onClick={() => setPrintData(null)}
                    className="p-2.5 bg-white text-gray-500 rounded-xl border border-gray-200 hover:bg-gray-50 transition-all"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-inner overflow-x-auto print-high-fidelity">
                <div className="hidden print:block">
                  <OfficialBillPrint 
                    student={printData.student} 
                    payment={printData.payment} 
                    items={billItems} 
                    paymentMethod={paymentMethod}
                    printedBy={user?.full_name || user?.username || 'Admin'} 
                    isPrintView={true} 
                  />
                </div>
                <div className="print:hidden flex justify-center">
                  <OfficialBillPrint 
                    student={printData.student} 
                    payment={printData.payment} 
                    items={billItems} 
                    paymentMethod={paymentMethod}
                    onPaymentMethodChange={setPaymentMethod}
                    printedBy={user?.full_name || user?.username || 'Admin'} 
                    isPrintView={false}
                    onItemChange={(idx, field, val) => {
                      const newItems = [...billItems];
                      newItems[idx][field] = val;
                      setBillItems(newItems);
                    }}
                    onAddItem={() => setBillItems([...billItems, { description: '', amount: '0' }])}
                    onRemoveItem={(idx) => {
                      const newItems = [...billItems];
                      newItems.splice(idx, 1);
                      setBillItems(newItems);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="hidden print:block official-view">
        {printData && <OfficialBillPrint student={printData.student} payment={printData.payment} items={billItems} paymentMethod={paymentMethod} printedBy={user?.full_name || user?.username || 'Admin'} isPrintView={true} />}
      </div>
    </div>
  );
};

export default Payments;
